import { styled } from "@mui/material";
import { NavLink } from "react-router";

import { FULL_VIEWPORT } from "@citrine/atoms/fill-viewport";

import { CatalystExpanded } from "#src/components/catalyst/catalyst.sx";

interface PageProps {
  hasProjectToolbar?: boolean;
}
export const Page = styled("div", {
  shouldForwardProp: (propName: keyof PageProps) =>
    propName !== "hasProjectToolbar",
})<PageProps>(({ theme, hasProjectToolbar }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  padding: theme.spacing(theme.layout.BODY_MARGIN),
  transition: theme.transitions.create(["margin", "padding"]),

  /**
   * This is the maximum content height viewable
   * When inside a project and with a tall enough screen, the project toolbar
   * must also be accounted for
   */
  [FULL_VIEWPORT]: `calc(100vh - ${theme.spacing(theme.layout.NAVBAR_HEIGHT + 2 * theme.layout.BODY_MARGIN)})`,
  [`@media (min-height: ${theme.spacing(theme.layout.SCREEN_BREAK_1)})`]:
    hasProjectToolbar
      ? {
          [FULL_VIEWPORT]: `calc(100vh - ${theme.spacing(theme.layout.NAVBAR_HEIGHT + theme.layout.PROJECT_TOOLBAR_HEIGHT + 2 * theme.layout.BODY_MARGIN)})`,
        }
      : undefined,

  // if the page is wide enough, leave room for catalyst to the left
  [`@media (min-width: ${theme.spacing(
    theme.layout.CATALYST_WIDTH + theme.layout.SCREEN_BREAK_1
  )})`]: {
    [`${CatalystExpanded} ~ &`]: {
      marginLeft: theme.spacing(theme.layout.CATALYST_WIDTH),
      padding: theme.spacing(theme.layout.BODY_MARGIN),
    },
  },

  /**
   * I originally wrote these padding changes as @container queries.
   * It's definitely cleaner than duplicating each query with CATALYST_WIDTH.
   *
   * However, since expanding the catalyst panel itself is animated, the
   * container's width may not cross a threshold until the end of its
   * animation. That would then trigger a subsequent padding animation.
   *
   * The result is a wonky bubble effect. The best way to avoid it is to
   * trigger the padding animation immediately based on the expanded state.
   */

  // increase padding with page width to focus
  [`@media (min-width: ${theme.spacing(theme.layout.SCREEN_BREAK_2)})`]: {
    padding: theme.spacing(
      theme.layout.BODY_MARGIN,
      2 * theme.layout.BODY_MARGIN
    ),
  },
  [`@media (min-width: ${theme.spacing(theme.layout.SCREEN_BREAK_3)})`]: {
    padding: theme.spacing(
      theme.layout.BODY_MARGIN,
      3 * theme.layout.BODY_MARGIN
    ),
  },
  [`@media (min-width: ${theme.spacing(theme.layout.SCREEN_BREAK_4)})`]: {
    padding: theme.spacing(
      theme.layout.BODY_MARGIN,
      5 * theme.layout.BODY_MARGIN
    ),
  },

  // adjust padding values when catalyst is expanded
  [`@media (min-width: ${theme.spacing(
    theme.layout.CATALYST_WIDTH + theme.layout.SCREEN_BREAK_2
  )})`]: {
    [`${CatalystExpanded} ~ &`]: {
      padding: theme.spacing(
        theme.layout.BODY_MARGIN,
        2 * theme.layout.BODY_MARGIN
      ),
    },
  },
  [`@media (min-width: ${theme.spacing(
    theme.layout.CATALYST_WIDTH + theme.layout.SCREEN_BREAK_3
  )})`]: {
    [`${CatalystExpanded} ~ &`]: {
      padding: theme.spacing(
        theme.layout.BODY_MARGIN,
        3 * theme.layout.BODY_MARGIN
      ),
    },
  },
  [`@media (min-width: ${theme.spacing(
    theme.layout.CATALYST_WIDTH + theme.layout.SCREEN_BREAK_4
  )})`]: {
    [`${CatalystExpanded} ~ &`]: {
      padding: theme.spacing(
        theme.layout.BODY_MARGIN,
        5 * theme.layout.BODY_MARGIN
      ),
    },
  },
}));

export const AccountLink = styled(NavLink)(({ theme }) => ({
  color: theme.palette.common.white,
  display: "flex",
  margin: theme.spacing(-2),
  padding: theme.spacing(2),
  textDecoration: "none",
}));
